class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;

        // Hide sticky cart before update
        const stickyCartIcon = document.getElementById('cart-icon-product-page');
        if (stickyCartIcon) {
          stickyCartIcon.style.opacity = '0';
          stickyCartIcon.style.transition = 'opacity 0.2s ease-out';
        }

        // Update both cart icons independently
        Promise.all([
          fetch(`${routes.cart_url}?section_id=cart-icon-bubble`),
          fetch(`${routes.cart_url}?section_id=sticky-cart`)
        ])
          .then(([headerResponse, stickyResponse]) => 
            Promise.all([headerResponse.text(), stickyResponse.text()])
          )
          .then(([headerHtml, stickyHtml]) => {
            // Update header cart icon
            const headerCartIcon = document.getElementById('cart-icon-bubble');
            if (headerCartIcon) {
              headerCartIcon.innerHTML = this.getSectionInnerHTML(headerHtml, '.shopify-section');
            }
            
            // Update sticky cart icon with its own structure
            if (stickyCartIcon) {
              const stickyCartParent = stickyCartIcon.parentNode;
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = this.getSectionInnerHTML(stickyHtml, '.shopify-section');
              const newStickyCart = tempDiv.querySelector('#cart-icon-product-page');
              if (newStickyCart) {
                newStickyCart.style.opacity = '0';
                stickyCartParent.replaceChild(newStickyCart, stickyCartIcon);
                // Reinitialize cart drawer binding
                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer) {
                  cartDrawer.setHeaderCartIconAccessibility();
                }
                requestAnimationFrame(() => {
                  newStickyCart.style.opacity = '1';
                  newStickyCart.style.transition = 'opacity 0.2s ease-out';
                });
              }
            }
          });
      })
      .catch(e => {
        // Show sticky cart in case of error
        const stickyCartIcon = document.getElementById('cart-icon-product-page');
        if (stickyCartIcon) {
          stickyCartIcon.style.opacity = '1';
        }
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-product-page',
        section: 'sticky-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    // Hide sticky cart during quantity update
    const stickyCartIcon = document.getElementById('cart-icon-product-page');
    if (stickyCartIcon) {
      stickyCartIcon.style.opacity = '0';
      stickyCartIcon.style.transition = 'opacity 0.2s ease-out';
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');
        const errorElement = document.getElementById(`CartDrawer-LineItemError-${line}`) || document.getElementById(`Line-item-error-${line}`);

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          errorElement.classList.add('cart-item__error--is-visible');
          return;
        } else{
          errorElement.classList.remove('cart-item__error--is-visible');
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          if (section.id === 'cart-icon-bubble') {
            // Update header cart icon
            const headerCartIcon = document.getElementById('cart-icon-bubble');
            if (headerCartIcon) {
              headerCartIcon.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], '.shopify-section');
            }
          } else if (section.id === 'cart-icon-product-page') {
            // Update sticky cart icon with its own structure
            const stickyCartIcon = document.getElementById('cart-icon-product-page');
            if (stickyCartIcon) {
              const stickyCartParent = stickyCartIcon.parentNode;
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], '.shopify-section');
              const newStickyCart = tempDiv.querySelector('#cart-icon-product-page');
              if (newStickyCart) {
                newStickyCart.style.opacity = '0';
                stickyCartParent.replaceChild(newStickyCart, stickyCartIcon);
                // Reinitialize cart drawer binding
                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer) {
                  cartDrawer.setHeaderCartIconAccessibility();
                }
                requestAnimationFrame(() => {
                  newStickyCart.style.opacity = '1';
                  newStickyCart.style.transition = 'opacity 0.2s ease-out';
                });
              }
            }
          } else {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            if (elementToReplace) {
              elementToReplace.innerHTML =
                this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            }
          }
        }));
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items'});
      })
      .catch(() => {
        // Show sticky cart in case of error
        if (stickyCartIcon) {
          stickyCartIcon.style.opacity = '1';
        }
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
      constructor() {
        super();

      this.addEventListener('change', debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, ON_CHANGE_DEBOUNCE_TIMER))
      }
  });
};
